import { useCallback, useEffect, useSyncExternalStore } from 'react'

/**
 * The persisted theme *choice* — what the user picked. `'auto'` defers to the
 * OS via `prefers-color-scheme` (Story 10.1). This is the value stored under
 * `sg.theme` and shown in the segmented control. It is NOT what lands on the
 * `data-theme` attribute: `'auto'` is always resolved to a concrete theme first.
 */
export type Theme = 'light' | 'dark' | 'auto'

/** The concrete theme actually applied to `<html data-theme>` — never `'auto'`. */
export type ResolvedTheme = 'light' | 'dark'

const STORAGE_KEY = 'sg.theme'
const TRANSITION_CLASS = 'theme-transition'

/** True when the OS currently prefers a dark color scheme. */
function prefersDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-color-scheme: dark)').matches === true
  )
}

/**
 * Resolve a stored *choice* to a concrete theme. `'auto'` (and anything not a
 * valid literal) defers to `prefers-color-scheme`; `'light'`/`'dark'` pass
 * through. The result is what `data-theme` should be — never `'auto'`.
 */
export function resolveTheme(choice: Theme): ResolvedTheme {
  if (choice === 'light' || choice === 'dark') return choice
  return prefersDark() ? 'dark' : 'light'
}

/**
 * Read the persisted *choice* from localStorage. This is the source of truth
 * for the segmented control (AC-2): after the anti-FOUC script runs, the DOM
 * `data-theme` is already concrete, so reading the attribute would lose the
 * `'auto'` choice and show the wrong segment on reload. We read localStorage
 * instead.
 *
 * The DEFAULT (no stored value) is `'auto'` — NOT `'light'`. This matches the
 * anti-FOUC inline script (which resolves a missing value via
 * `prefers-color-scheme`) exactly, so a fresh load on a dark-OS machine never
 * flashes dark→light, and a new user follows their OS preference (the prior
 * behaviour before Story 10.1 added the explicit choice).
 */
function readStoredChoice(): Theme {
  if (typeof window === 'undefined') return 'auto'
  let stored: string | null = null
  try {
    stored = window.localStorage.getItem(STORAGE_KEY)
  } catch {
    /* localStorage unavailable — fall through to default */
  }
  if (stored === 'light' || stored === 'dark' || stored === 'auto') return stored
  return 'auto'
}

/**
 * Story 23.7 — as páginas PÚBLICAS (o catálogo da vaga) têm tema próprio: abrem
 * em escuro e guardam a escolha à parte (`use-public-theme.ts`). Enquanto uma
 * delas está montada, `<html data-theme-scope="public">` diz ao tema da APP que
 * não é dono do atributo — sem isto, o `Toaster` montado na raiz reaplicava o
 * tema da app ao montar e desfazia o escuro da página pública.
 */
export function isPublicThemeScope(): boolean {
  return typeof document !== 'undefined' && document.documentElement.dataset.themeScope === 'public'
}

/** Apply a concrete theme to `<html>`, optionally with the transition class. */
function applyResolved(resolved: ResolvedTheme, animate: boolean): void {
  if (typeof document === 'undefined') return
  if (isPublicThemeScope()) return
  const html = document.documentElement
  if (animate) {
    html.classList.add(TRANSITION_CLASS)
    window.setTimeout(() => html.classList.remove(TRANSITION_CLASS), 300)
  }
  html.setAttribute('data-theme', resolved)
}

// ----------------------------------------------------------------------------
// Module-level store — a SINGLE source of truth for the theme choice shared by
// every `useTheme` consumer. Without this, two mounted controls (the sidebar
// popover + the /settings Aparência section) would each keep their own
// `useState` and drift: changing one would flip `data-theme` globally but leave
// the other's highlighted segment stale (AC-9 requires them to stay in sync).
//
// Implemented with `useSyncExternalStore` (no new dependency): one store, every
// instance subscribes, a `setTheme` re-renders them all in lockstep.
// ----------------------------------------------------------------------------

let currentChoice: Theme = readStoredChoice()
const listeners = new Set<() => void>()

/** Live OS-preference media query — registered lazily while the choice is 'auto'. */
let mediaQuery: MediaQueryList | null = null

function emit(): void {
  listeners.forEach((l) => l())
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function getSnapshot(): Theme {
  return currentChoice
}

/** SSR snapshot — no DOM/localStorage, so the safe default. */
function getServerSnapshot(): Theme {
  return 'auto'
}

/** While the choice is 'auto', re-apply data-theme when the OS preference flips. */
function onOsPreferenceChange(): void {
  if (currentChoice !== 'auto') return
  applyResolved(prefersDark() ? 'dark' : 'light', true)
}

/** Attach the matchMedia listener (idempotent) — only relevant while 'auto'. */
function attachOsListener(): void {
  if (typeof window === 'undefined' || !window.matchMedia) return
  if (mediaQuery) return
  mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
  mediaQuery.addEventListener('change', onOsPreferenceChange)
}

/** Detach the matchMedia listener (idempotent) — when leaving 'auto'. */
function detachOsListener(): void {
  if (!mediaQuery) return
  mediaQuery.removeEventListener('change', onOsPreferenceChange)
  mediaQuery = null
}

/**
 * Set the theme choice: persist it, apply the resolved concrete theme, manage
 * the OS listener (only live while 'auto'), and notify every subscriber so all
 * mounted controls re-render together.
 */
function setThemeChoice(next: Theme): void {
  currentChoice = next
  applyResolved(resolveTheme(next), true)
  try {
    window.localStorage.setItem(STORAGE_KEY, next)
  } catch {
    /* localStorage unavailable — silent */
  }
  if (next === 'auto') attachOsListener()
  else detachOsListener()
  emit()
}

/**
 * Theme hook — light/dark/auto choice persisted in localStorage under `sg.theme`,
 * backed by a shared module store so all consumers stay in lockstep (AC-9).
 *
 * Three-part `'auto'` contract (Story 10.1 AC-3/AC-4/AC-5):
 *   1. The *choice* (`'auto'` included) is persisted; the *applied* `data-theme`
 *      is always the concrete `resolveTheme(choice)` — never the literal `'auto'`.
 *   2. While the choice is `'auto'`, a `matchMedia('(prefers-color-scheme: dark)')`
 *      listener re-applies `data-theme` live when the OS preference flips — WITHOUT
 *      changing the stored choice. The listener is removed when the choice changes
 *      to `'light'`/`'dark'`.
 *   3. The anti-FOUC inline script in `index.html` resolves `'auto'`/missing via
 *      `prefers-color-scheme` before first paint (the only FOUC-prevention point).
 */
export function useTheme(): {
  /** The persisted choice (light/dark/auto) — drives the segmented control. */
  theme: Theme
  setTheme: (theme: Theme) => void
  toggleTheme: () => void
} {
  const theme = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)

  // Apply the resolved concrete theme on mount and keep the OS listener live
  // while the (shared) choice is 'auto'. Runs once per mount; the store handles
  // ongoing updates. The default choice 'auto' matches the anti-FOUC script, so
  // there is no first-paint flash on a fresh load.
  useEffect(() => {
    applyResolved(resolveTheme(currentChoice), false)
    if (currentChoice === 'auto') attachOsListener()
  }, [])

  const setTheme = useCallback((next: Theme) => {
    setThemeChoice(next)
  }, [])

  const toggleTheme = useCallback(() => {
    // Binary toggle preserved for any legacy caller: cycles concrete light/dark
    // based on what is currently applied (auto resolves first).
    setThemeChoice(resolveTheme(currentChoice) === 'dark' ? 'light' : 'dark')
  }, [])

  return { theme, setTheme, toggleTheme }
}

/** Reaplica o tema da APP — o que uma página pública chama ao desmontar. */
export function reapplyAppTheme(): void {
  applyResolved(resolveTheme(readStoredChoice()), false)
}

/**
 * Test-only reset — clears the module store back to the value in localStorage
 * and tears down any OS listener, so each test starts from a clean slate. Not
 * used by application code (the store is process-lived in the browser).
 */
export function __resetThemeStoreForTests(): void {
  detachOsListener()
  currentChoice = readStoredChoice()
  emit()
}
